import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { guideApi, AdminGuideItem, GuideCategory, GUIDE_CATEGORY_LABELS, CreateGuideRequest, UpdateGuideRequest } from '../../api/guide.api';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Pencil, Trash2, Plus, Check, X } from 'lucide-react';

/** 카테고리 뱃지 색상 */
const CATEGORY_COLORS: Record<GuideCategory, string> = {
  SURVEY_GUIDELINE: 'bg-blue-100 text-blue-700',
  REWARD_RULE: 'bg-amber-100 text-amber-700',
  ATTENDANCE_RULE: 'bg-green-100 text-green-700',
};

export const GuideManagement = () => {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<GuideCategory | 'ALL'>('ALL');

  // 인라인 편집 상태
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editContent, setEditContent] = useState('');
  const [editSortOrder, setEditSortOrder] = useState(0);

  // 새 문구 추가 상태
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCategory, setNewCategory] = useState<GuideCategory>('SURVEY_GUIDELINE');
  const [newContent, setNewContent] = useState('');
  const [newSortOrder, setNewSortOrder] = useState(0);

  /** API 호출 */
  const { data: guides = [], isLoading } = useQuery({
    queryKey: ['adminGuides'],
    queryFn: () => guideApi.getAll().then(res => res.data.results),
  });

  const createMutation = useMutation({
    mutationFn: (params: CreateGuideRequest) => guideApi.create(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminGuides'] });
      resetAddForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, params }: { id: number; params: UpdateGuideRequest }) => guideApi.update(id, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminGuides'] });
      setEditingId(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => guideApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminGuides'] });
    },
  });

  /** 활성/비활성 토글 */
  const toggleActive = (item: AdminGuideItem) => {
    updateMutation.mutate({ id: item.id, params: { isActive: !item.isActive } });
  };

  /** 인라인 편집 시작 */
  const startEdit = (item: AdminGuideItem) => {
    setEditingId(item.id);
    setEditContent(item.content);
    setEditSortOrder(item.sortOrder);
  };

  /** 인라인 편집 저장 */
  const saveEdit = () => {
    if (editingId === null) return;
    updateMutation.mutate({
      id: editingId,
      params: { content: editContent, sortOrder: editSortOrder },
    });
  };

  /** 새 문구 추가 폼 초기화 */
  const resetAddForm = () => {
    setShowAddForm(false);
    setNewContent('');
    setNewSortOrder(0);
  };

  /** 새 문구 저장 */
  const handleCreate = () => {
    if (!newContent.trim()) return;
    createMutation.mutate({
      category: newCategory,
      content: newContent.trim(),
      sortOrder: newSortOrder,
      isActive: true,
    });
  };

  /** 삭제 확인 */
  const handleDelete = (id: number) => {
    if (window.confirm('이 안내문구를 삭제하시겠습니까?')) {
      deleteMutation.mutate(id);
    }
  };

  /** 필터링된 목록 */
  const filteredGuides = filter === 'ALL'
    ? guides
    : guides.filter((g: AdminGuideItem) => g.category === filter);

  /** 날짜 포맷 */
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <div className="space-y-4">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">안내문구 관리</h2>
        <Button
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center gap-1.5"
          size="sm"
        >
          <Plus className="w-4 h-4" />
          새 문구 추가
        </Button>
      </div>

      {/* 카테고리 필터 */}
      <div className="flex gap-2">
        <Button
          variant={filter === 'ALL' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('ALL')}
        >
          전체
        </Button>
        {(Object.keys(GUIDE_CATEGORY_LABELS) as GuideCategory[]).map(cat => (
          <Button
            key={cat}
            variant={filter === cat ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(cat)}
          >
            {GUIDE_CATEGORY_LABELS[cat]}
          </Button>
        ))}
      </div>

      {/* 새 문구 추가 폼 */}
      {showAddForm && (
        <Card className="p-4">
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700 w-16">카테고리</label>
              <select
                value={newCategory}
                onChange={e => setNewCategory(e.target.value as GuideCategory)}
                className="border rounded-md px-3 py-1.5 text-sm"
              >
                {(Object.keys(GUIDE_CATEGORY_LABELS) as GuideCategory[]).map(cat => (
                  <option key={cat} value={cat}>{GUIDE_CATEGORY_LABELS[cat]}</option>
                ))}
              </select>
              <label className="text-sm font-medium text-gray-700 ml-2">순서</label>
              <input
                type="number"
                value={newSortOrder}
                onChange={e => setNewSortOrder(Number(e.target.value))}
                className="border rounded-md px-3 py-1.5 text-sm w-20"
              />
            </div>
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700 w-16">내용</label>
              <input
                type="text"
                value={newContent}
                onChange={e => setNewContent(e.target.value)}
                placeholder="안내문구를 입력하세요"
                className="border rounded-md px-3 py-1.5 text-sm flex-1"
                onKeyDown={e => e.key === 'Enter' && handleCreate()}
              />
              <Button size="sm" onClick={handleCreate} disabled={createMutation.isPending}>
                <Check className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="outline" onClick={resetAddForm}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* 테이블 */}
      <Card>
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">로딩 중...</div>
        ) : filteredGuides.length === 0 ? (
          <div className="p-8 text-center text-gray-500">등록된 안내문구가 없습니다.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50/50">
                  <th className="px-4 py-3 text-left font-medium text-gray-500 w-16">순서</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500 w-32">카테고리</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">문구 내용</th>
                  <th className="px-4 py-3 text-center font-medium text-gray-500 w-20">활성</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500 w-40">수정일</th>
                  <th className="px-4 py-3 text-center font-medium text-gray-500 w-24">액션</th>
                </tr>
              </thead>
              <tbody>
                {filteredGuides.map((item: AdminGuideItem) => (
                  <tr key={item.id} className="border-b hover:bg-gray-50/50 transition-colors">
                    {/* 순서 */}
                    <td className="px-4 py-3">
                      {editingId === item.id ? (
                        <input
                          type="number"
                          value={editSortOrder}
                          onChange={e => setEditSortOrder(Number(e.target.value))}
                          className="border rounded px-2 py-1 text-sm w-14"
                        />
                      ) : (
                        <span className="text-gray-600">{item.sortOrder}</span>
                      )}
                    </td>
                    {/* 카테고리 */}
                    <td className="px-4 py-3">
                      <Badge className={CATEGORY_COLORS[item.category]}>
                        {item.categoryDescription}
                      </Badge>
                    </td>
                    {/* 문구 내용 */}
                    <td className="px-4 py-3">
                      {editingId === item.id ? (
                        <input
                          type="text"
                          value={editContent}
                          onChange={e => setEditContent(e.target.value)}
                          className="border rounded px-2 py-1 text-sm w-full"
                          onKeyDown={e => {
                            if (e.key === 'Enter') saveEdit();
                            if (e.key === 'Escape') setEditingId(null);
                          }}
                          autoFocus
                        />
                      ) : (
                        <span className="text-gray-900">{item.content}</span>
                      )}
                    </td>
                    {/* 활성 */}
                    <td className="px-4 py-3 text-center">
                      <Switch
                        checked={item.isActive}
                        onCheckedChange={() => toggleActive(item)}
                      />
                    </td>
                    {/* 수정일 */}
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {formatDate(item.modDateTime)}
                    </td>
                    {/* 액션 */}
                    <td className="px-4 py-3 text-center">
                      {editingId === item.id ? (
                        <div className="flex items-center justify-center gap-1">
                          <Button size="sm" variant="ghost" onClick={saveEdit}>
                            <Check className="w-4 h-4 text-green-600" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                            <X className="w-4 h-4 text-gray-400" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-1">
                          <Button size="sm" variant="ghost" onClick={() => startEdit(item)}>
                            <Pencil className="w-4 h-4 text-gray-400" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(item.id)}>
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};
